import { useEffect, useRef, useState } from 'react';

/**
 * A search field whose text the server searches: it writes the address after a pause in typing,
 * and follows the address when something else changes it — «Azzera i filtri», or going back —
 * instead of writing its old text over it a moment later. It remembers what it wrote itself, so
 * its own write never resets what is being typed (a space typed before the pause stays).
 */
export function useSearchField(
  q: string,
  write: (q: string) => void,
  delayMs = 300,
): readonly [string, (typed: string) => void] {
  const [typed, setTyped] = useState(q);
  const written = useRef(q);

  useEffect(() => {
    if (q !== written.current) {
      written.current = q;
      setTyped(q);
    }
  }, [q]);

  useEffect(() => {
    const next = typed.trim();
    if (next === q) {
      return;
    }
    const timer = setTimeout(() => {
      written.current = next;
      write(next);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [typed, q, write, delayMs]);

  return [typed, setTyped];
}
